import type { PlannedItem, WeeklyCommitment } from '~/models/fitnessDomain'

/**
 * Turning one planned item into the three versions of a week.
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  SOLA SHAPES THE WEEK. NOBODY ELSE DOES, INCLUDING THE REVIEWER
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  Shared by the setup starter plan (D-101) and by a Gemini suggestion's pre-fill
 *  (D-093 §6), and the sharing is the point rather than a convenience. **A
 *  suggestion carries a session count and nothing else** — no short week and no
 *  floor — so whoever proposed the count, the other two variants come from the
 *  same rule.
 *
 *  **The floor especially must not come from a reviewer.** `minimumViable` is the
 *  safety valve that makes a bad week survivable rather than failed; a model
 *  setting it would be making a coaching decision behind a field the user reads
 *  as descriptive. Sola derives it, always, from the count in front of it.
 *
 *  **Decrements, not fractions.** One session fewer for a short week, exactly one
 *  for the floor. A fraction of the week would be a ratio somebody chose and would
 *  need approving in `rules/thresholds.ts`; one session is the smallest step a
 *  `PlannedItem` can express, which is why neither of these is a threshold.
 */
export function weekAround(item: PlannedItem): WeeklyCommitment {
  return {
    standard: [item],
    /* Equal to the standard week when that is already one session. There is no
       smaller non-empty week left except the floor below, and inventing one
       would mean a zero, which the model rejects. */
    short: [{ ...item, timesPerWeek: Math.max(1, item.timesPerWeek - 1) }],
    /* Always exactly one, never the standard count carried through: a floor equal
       to the plan is no floor at all in the one case a floor exists for. */
    minimumViable: [{ ...item, timesPerWeek: 1 }],
  }
}
